import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';

import { IHbciAccount } from '../Interfaces/IHbciAccount';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalRole } from '../Interfaces/ILegalRole';
import { useUpdateHbciAccount } from '../hooks/reactQuerys/HbciAccountQueries';
import { useAlert } from '../context/AlertContext';

interface Props {
  open: boolean;
  idLegalTransaction: number;
  account: IHbciAccount;
  participants: ILegalClient[];
  roles: ILegalRole[];
  onClose: () => void;
  /** Wird nach erfolgreichem Speichern aufgerufen (z. B. zum Refetch). */
  onSaved?: () => void;
}

/**
 * Bearbeitet die Sichtbarkeit eines bestehenden Notaranderkontos im
 * Mandantenportal: optionaler Einzel-Mandant und/oder Rollen-Einschränkung.
 * Leere Rollen-Auswahl = für alle Mandanten des Vorgangs sichtbar.
 */
export const HbciAccountVisibilityDialog: React.FC<Props> = ({
  open,
  idLegalTransaction,
  account,
  participants,
  roles,
  onClose,
  onSaved,
}) => {
  const { showAlert } = useAlert();
  const { updateHbciAccount, isPendingUpdateHbciAccount } =
    useUpdateHbciAccount(idLegalTransaction);

  const [selectedClient, setSelectedClient] = useState<number | ''>('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  // Bei Öffnen mit den aktuellen Werten des Kontos vorbelegen.
  useEffect(() => {
    if (!open) return;
    setSelectedClient(account.LegalClient_idLegalClient ?? '');
    setSelectedRoleIds(account.LegalRoleIds ?? []);
  }, [open, account]);

  const handleSave = async () => {
    try {
      await updateHbciAccount({
        id: account.idHbciAccount,
        body: {
          // Bewusst null (nicht undefined) senden, wenn kein Mandant gewählt
          // ist — so wird eine bestehende Einzel-Verknüpfung gezielt gelöscht.
          LegalClient_idLegalClient:
            typeof selectedClient === 'number' ? selectedClient : null,
          LegalRoleIds: selectedRoleIds,
        },
      });
      showAlert({ text: 'Sichtbarkeit aktualisiert.', severity: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `Speichern fehlgeschlagen: ${e.message}`
            : 'Speichern fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Sichtbarkeit im Mandantenportal</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Konto <strong>{account.IBAN}</strong> — legen Sie fest, wer den
          Tracker im Mandantenportal sehen darf. Änderungen wirken sofort:
          Mandanten, die nicht mehr berechtigt sind, verlieren den Eintrag.
        </DialogContentText>

        {participants.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id='hbci-vis-client-label'>
              Mandant (optional)
            </InputLabel>
            <Select
              labelId='hbci-vis-client-label'
              label='Mandant (optional)'
              value={selectedClient}
              onChange={e =>
                setSelectedClient(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
            >
              <MenuItem value=''>– keine Einzel-Verknüpfung –</MenuItem>
              {participants.map(p => (
                <MenuItem key={p.idLegalClient} value={p.idLegalClient}>
                  {[p.FirstName, p.LastName].filter(Boolean).join(' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {roles.length > 0 && (
          <FormControl fullWidth>
            <InputLabel id='hbci-vis-role-label'>
              Sichtbar für Rolle(n)
            </InputLabel>
            <Select<number[]>
              labelId='hbci-vis-role-label'
              multiple
              value={selectedRoleIds}
              onChange={e =>
                setSelectedRoleIds(
                  (typeof e.target.value === 'string'
                    ? e.target.value.split(',').map(Number)
                    : e.target.value) as number[]
                )
              }
              input={<OutlinedInput label='Sichtbar für Rolle(n)' />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map(id => {
                    const r = roles.find(role => role.idLegalRole === id);
                    return (
                      <Chip
                        key={id}
                        size='small'
                        label={r?.LegalRole ?? `#${id}`}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {roles.map(r => (
                <MenuItem key={r.idLegalRole} value={r.idLegalRole}>
                  <Checkbox
                    checked={selectedRoleIds.indexOf(r.idLegalRole) > -1}
                  />
                  <ListItemText primary={r.LegalRole} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Leer lassen = für alle Mandanten des Vorgangs sichtbar. Sonst
              sehen nur Mandanten mit einer der gewählten Rollen das Konto.
            </FormHelperText>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPendingUpdateHbciAccount}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={isPendingUpdateHbciAccount}
          startIcon={
            isPendingUpdateHbciAccount ? (
              <CircularProgress size={16} />
            ) : undefined
          }
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};
